import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';

/** Helpers for the sign-in page's motion (the components are in Motion.tsx). Used by the sign-in, join and dashboard pages. */

export const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Counts up from 0 to `target` once it is known. */
export function useCountUp(target: number, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target || prefersReducedMotion()) {
      setValue(target);
      return undefined;
    }
    let frame = 0;
    const started = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - started) / duration);
      // Ease out: quick at first, settling on the number.
      setValue(Math.round(target * (1 - (1 - progress) ** 3)));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

/** A soft light that follows the mouse over the panel (mouse and pen only, not touch). */
export function useSpotlight() {
  const ref = useRef<HTMLElement>(null);
  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el || event.pointerType === 'touch') return;
    const box = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${event.clientX - box.left}px`);
    el.style.setProperty('--my', `${event.clientY - box.top}px`);
    el.style.setProperty('--spot', '1');
  };
  const onPointerLeave = () => ref.current?.style.setProperty('--spot', '0');
  return { ref, onPointerMove, onPointerLeave };
}

/** "Good morning" / "Good afternoon" / "Good evening" for the visitor's own clock. */
export function greeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Index-based delay for staggered children (`style={stagger(i)}`). */
export const stagger = (i: number): CSSProperties => ({ '--i': i }) as CSSProperties;

/**
 * Types each phrase out, holds it, deletes it, then moves to the next: for a placeholder that
 * suggests what to write. Paused while `paused` (e.g. the box has text). Reduced motion: the first
 * phrase, still.
 */
export function useTypewriter(phrases: string[], paused = false) {
  const [text, setText] = useState(phrases[0] ?? '');
  useEffect(() => {
    if (paused || phrases.length === 0 || prefersReducedMotion()) {
      setText(phrases[0] ?? '');
      return undefined;
    }
    let phrase = 0;
    let length = 0;
    let deleting = false;
    let timer = 0;
    const tick = () => {
      const full = phrases[phrase];
      if (!deleting) {
        length += 1;
        setText(full.slice(0, length));
        if (length >= full.length) {
          deleting = true;
          timer = window.setTimeout(tick, 2200);
          return;
        }
        timer = window.setTimeout(tick, 38 + Math.random() * 40);
      } else {
        length -= 2;
        setText(full.slice(0, Math.max(0, length)));
        if (length <= 0) {
          deleting = false;
          phrase = (phrase + 1) % phrases.length;
          timer = window.setTimeout(tick, 350);
          return;
        }
        timer = window.setTimeout(tick, 18);
      }
    };
    setText('');
    timer = window.setTimeout(tick, 600);
    return () => window.clearTimeout(timer);
  }, [phrases, paused]);
  return text;
}

/**
 * Types `text` out once (again whenever it changes); `done` turns true at the end so what follows
 * can appear. Reduced motion: the whole text at once.
 */
export function useTypeOnce(text: string, speedMs = 22) {
  const [shown, setShown] = useState(text);
  useEffect(() => {
    if (prefersReducedMotion() || !text) {
      setShown(text);
      return undefined;
    }
    let length = 0;
    setShown('');
    const timer = window.setInterval(() => {
      length += 1;
      setShown(text.slice(0, length));
      if (length >= text.length) window.clearInterval(timer);
    }, speedMs);
    return () => window.clearInterval(timer);
  }, [text, speedMs]);
  return { shown, done: shown.length >= text.length };
}
